import { useState, useEffect } from "react";
import "./profile.css";
import { Person } from "@mui/icons-material";
import DoubleArrowOutlinedIcon from "@mui/icons-material/DoubleArrowOutlined";
import { useAuth } from "@clerk/clerk-react";
import Loader from "../components/loader";
import { Link, useNavigate } from "react-router-dom";
import SecurityIcon from '@mui/icons-material/Security';

export default function Profile() {
  const navigate = useNavigate();
  const [page, setPage] = useState("me");
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [flowData, setFlowData] = useState(null);
  const { getToken } = useAuth();
  const [flowLoading, setFlowLoading] = useState(true);
  const handleOpen = (index) => {
    const skillGapData = flowData.skillgap[index].skillgap;
    const roadmapData = flowData.roadmap[index].roadmap;
    const flow_name = flowData.roadmap[index].roadmap.flow_name;
    const flow_id = flowData.flow_id[index]["flow_id"];
    const source = "profile"
    navigate("/dashboard", { state: { skillGapData, roadmapData, flow_name, source, flow_id } });
  }

  const FlowWidget = ({ data }) => {
    if (!data || !data.roadmap) return <div>No data available</div>;
    const flows = data.roadmap.map((item, index) => {
      const flowName = item?.roadmap?.flow_name;
      if (!flowName) return null;
      return (
        <div className="flow_widget" key={index} onClick={() => { handleOpen(index) }}>
          <h3>{flowName}</h3>
          <div className="flow_widget_button">Open Flow</div>
        </div>
      );
    });
    return <div className="flow_container">{flows}</div>;
  };
  // getUserData URI: "https://jobsniper-be-production.up.railway.app/userinfo"
  const getUserData = async () => {
    try {
      setLoading(true);
      const token = await getToken();
      const res = await fetch("https://jobsniper-be-production.up.railway.app/userinfo", {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) {
        setUserData({ error: `Something went wrong: ${res.status}` });
      } else {
        const data = await res.json();
        setUserData(data);
        return userData;
      }
    } catch (err) {
      setUserData({ error: err.message });
    } finally {
      setLoading(false);
    }
  };
  // getUserFlows URL: https://jobsniper-be-production.up.railway.app/getflows
  const getUserFlows = async () => {
    try {
      setFlowLoading(true);
      const token = await getToken();
      const res = await fetch("https://jobsniper-be-production.up.railway.app/getflows", {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        }
      })
      const data = await res.json();
      setFlowLoading(false);
      setFlowData(data);
    }
    catch (err) {
      console.log(err);
    }
  }

  useEffect(() => {
    if (page === "me") getUserData();
    else if (page === "flows") getUserFlows();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  return (
    <section className="profile_parent">
      <div className="sidebar_parent">
        <div className="sidebar_container">
          <div className="sidebar_head">Account</div>
          <div className="sidebar_contents">
            <div className="sidebar_links" onClick={() => setPage("me")}>
              Your Profile <Person />
            </div>
            <div className="sidebar_links" onClick={() => setPage("flows")}>
              Saved Flows <DoubleArrowOutlinedIcon />
            </div>
            <div className="sidebar_links" onClick={()=>{setPage("policies")}}>
              Policies <SecurityIcon />
            </div>
          </div>
        </div>
      </div>

      <div className="profile_main">
        {page === "me" ? (
          <div className="me_parent">
            {loading && <Loader className="loader" />}
            {!loading && userData && userData.error && (
              <div>{userData.error}</div>
            )}
            {!loading && userData && !userData.error && (
              <div className="profile_container">
                <div className="profile_pic_container">
                  <img
                    className="profile_pic"
                    src={userData.user_image}
                    alt="user pic"
                  />
                </div>
                <div className="first_name">
                  {userData.first_name}
                  <div className="last_name">{userData.last_name ? userData.last_name : ""}</div>
                </div>
                <div className="email_address">{userData.email_address}</div>
              </div>
            )}
          </div>
        ) : page === "flows"?(
          <div className={flowLoading ? "flow_parent_loading" : "flow_parent"}>
            {flowLoading && <Loader className="loader" />}
            {!flowLoading && flowData && (
              <div className="flow_parent_container">
                <div className="flow_head">Created Flows</div>
                {
                  flowData["flow_id"].length === 0 ? 
                  <div className="no_flow_cta">
                    You have not created any flows yet, start creating now
                    <Link to="/analyzer">
                      <button className="grad_button">Start your analysis now</button>
                    </Link>
                  </div> 
                  :<FlowWidget data={flowData} />
                }
              </div>
            )}
          </div>
        ):page === "policies" ? (
          <div className="policies_parent">
            <h2>Cancellation & Refund Policy</h2>
            <p>
              JobSniper provides digital services delivered instantly after purchase.<br />
              Once analysis or flow creation begins, no refunds are issued.<br />
              If a user is charged but does not receive access due to a technical issue, they may request support within 24 hours at <b>jobsniperteam@gmail.com</b> and we will resolve or refund the issue.<br />
              We do not offer refunds for incorrect uploads, dissatisfaction with results, or change of mind.
            </p>

            <h2>Contact Us</h2>
            <p>
              For support or queries:<br />
              Email: <b>jobsniperteam@gmail.com</b><br />
              Location: Chennai, India<br />
              This is the official support channel for JobSniper.
            </p>

            <h2>Privacy Policy</h2>
            <p>
              JobSniper collects and processes user data such as resumes, job descriptions, and email addresses for the purpose of providing skill-gap analysis and generating personalized learning flows.<br />
              We do not sell or share your data with third parties except trusted service providers required to run the platform (analytics, hosting, payment).<br />
              Users can request data deletion anytime by contacting <b>jobsniperteam@gmail.com</b>.<br />
              By using JobSniper, you consent to this Privacy Policy.
            </p>

            <h2>Shipping & Delivery Policy</h2>
            <p>
              JobSniper is a digital service, and no physical goods are shipped.<br />
              All services, including resume analysis and flow generation, are delivered online and instantly after successful payment.<br /><br />
              <b>Delivery Time:</b> Access to premium features is provided immediately once the payment is confirmed.<br />
              <b>Shipping Charges:</b> There are no shipping or delivery charges, as no physical product is sent.<br />
              <b>Mode of Delivery:</b> Delivery is done digitally through the JobSniper web platform.<br />
              For any issues related to delivery of digital access, please contact us at <b>jobsniperteam@gmail.com</b>.
            </p>

            <h2>Terms & Conditions</h2>
            <p>
              By using JobSniper, you agree not to upload harmful, illegal, or copyrighted data you do not own.<br />
              JobSniper provides AI-based analysis and learning recommendations; outcomes are not guaranteed and are informational in nature.<br />
              Payments made grant access to premium features such as advanced analysis and generated learning flows.<br />
              Misuse of the platform may lead to account termination.<br />
              All disputes are subject to Indian jurisdiction.
            </p>
          </div>)
          :null
        }
      </div>

    </section>
  );
}
